import React, { FC, useState, useEffect, useCallback, useContext } from 'react';
import { useParams, useHistory } from 'react-router-dom';

import { Button, Grid, Typography, Box, Card, CardContent, Divider, Chip } from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ListAltIcon from '@mui/icons-material/ListAlt';

import DeleteConfirmationModal from '../../../sharedModules/layout/DeleteConfirmationModal';
import DocumentationModal from '../../../sharedModules/layout/DocumentationModal';
import ErrorMessage from '../../../sharedModules/layout/ErrorMessage';
import LoadingWrapper from '../../../sharedModules/layout/LoadingWrapper';
import PageHeader from '../../../sharedModules/layout/PageHeader';
import PlumbingIcon from '@mui/icons-material/Plumbing';
import UpdateForm from '../../../sharedModules/updateForm/UpdateForm';

import WaterAuthorityPdfUpload from '../components/WaterAuthorityPdfUpload';
import WaterAuthorityFieldDesignations from '../components/WaterAuthorityFieldDesignations';

import { DeleteConfirmationModalContext } from '../../../sharedModules/contexts/DeleteConfirmationModalContext';
import { waterAuthorityFormFields } from '../waterAuthorityFormConfig';

import axios from '../../../sharedModules/utils/axios.utils';

const designationDocumentationText = [
  'Here, you determine which values from our database will be mapped to the specific fillable fields in the PDF template.',
  'The `Customer Full Address` option populates the field with the address, city, state, and zip all on one line.',
  '`Backflow Type Text` displays the full name of the backflow type (Regular or DC). `Backflow Type Radio` selects the option button for the type of backflow.',
  '`Technician Signature` will show the .png image file for the tester if we have one.  If not, it will generate a signature of the testers name in a scrript font.',
  'If you need to remove a designation after choosing one, select the `None` option.',
  'To change the fields that are available in the "Authority Provided Fields" section, edit the fields in the "Water Authority Config" menu.',
];

const WaterAuthorityDetailPage: FC = () => {
  const { waterAuthorityId } = useParams<{ waterAuthorityId: string }>();
  const history = useHistory();

  const { setModelLabel, setDeleteConfirmationModalOpen, setRecordToDelete, setDeleteURL } =
    useContext(DeleteConfirmationModalContext);

  const [designationDocumentationModalOpen, setDesignationDocumentationModalOpen] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [waterAuthority, setWaterAuthority] = useState<any>({});
  const [refreshFieldsKey, setRefreshFieldsKey] = useState<number>(0);

  const fetchWaterAuthority = async () => {
    setLoading(true);
    try {
      const response: any = await axios.get(`/backflows/water_authorities/${waterAuthorityId}`);
      setWaterAuthority(response.data.result);
      setErrorMessage('');
    } catch (error) {
      setErrorMessage('Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  const handlePdfUploadSuccess = useCallback(() => {
    // Refresh the water authority data
    fetchWaterAuthority();
    // Trigger refresh of field designations by changing the key
    setRefreshFieldsKey((prev) => prev + 1);
  }, []);

  useEffect(() => {
    fetchWaterAuthority();
  }, []);

  const handleDeleteClicked = () => {
    console.log('handleDeleteClicked');
    setModelLabel('Water Authority');
    setDeleteConfirmationModalOpen(true);
    setRecordToDelete(waterAuthority);
    setDeleteURL('backflows/water_authorities/');
  };

  return (
    <LoadingWrapper isLoading={loading} message="Loading...">
      <DeleteConfirmationModal afterDelete={() => history.push('/backflow_tests/water_authority_configurations')} />
      <Box sx={{ bgcolor: '#f5f5f5', minHeight: '100vh', p: 3 }}>
        <Grid container spacing={4}>
          <Grid item xs={12}>
            <PageHeader
              titleIcon={<PlumbingIcon />}
              mainTitle="Water Authority Config"
              subTitle={waterAuthority?.name}
              backButtonLabel="Water Authorities List"
              backButtonLink="/backflow_tests/water_authority_configurations"
            />
          </Grid>

          {errorMessage && (
            <Grid item xs={12}>
              <ErrorMessage message={errorMessage} />
            </Grid>
          )}

          <Grid item xs={12}>
            <Box display="flex" alignItems="center" gap={2}>
              <Typography variant="h4" fontWeight="700">
                {waterAuthority?.name}
              </Typography>
              {waterAuthority?.status && (
                <Chip
                  label={waterAuthority.status}
                  color={waterAuthority.status === 'active' ? 'success' : 'default'}
                  size="medium"
                  sx={{ textTransform: 'capitalize', fontWeight: 600 }}
                />
              )}
            </Box>
          </Grid>

          <Grid item xs={12}>
            <Card elevation={2}>
              <CardContent>
                <Box display="flex" alignItems="center" mb={2}>
                  <PictureAsPdfIcon color="primary" sx={{ mr: 1 }} />
                  <Typography variant="h6" fontWeight="600">
                    PDF Template
                  </Typography>
                </Box>
                <Divider sx={{ mb: 3 }} />
                <WaterAuthorityPdfUpload waterAuthority={waterAuthority} onUploadSuccess={handlePdfUploadSuccess} />
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12}>
            <Card elevation={2}>
              <CardContent>
                <Box display="flex" alignItems="center" mb={2}>
                  <ListAltIcon color="primary" sx={{ mr: 1 }} />
                  <Typography variant="h6" fontWeight="600">
                    Fillable Field Designations
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
                    Map PDF form fields to data fields
                  </Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => setDesignationDocumentationModalOpen(true)}
                    sx={{
                      ml: 'auto',
                      bgcolor: 'white',
                      color: 'text.primary',
                      borderColor: 'rgba(0, 0, 0, 0.23)',
                      textTransform: 'none',
                      '&:hover': {
                        bgcolor: 'grey.50',
                        borderColor: 'rgba(0, 0, 0, 0.87)',
                      },
                    }}
                  >
                    Documentation
                  </Button>
                </Box>
                <Divider sx={{ mb: 3 }} />
                <WaterAuthorityFieldDesignations
                  key={refreshFieldsKey}
                  waterAuthority={waterAuthority}
                  fetchWaterAuthority={fetchWaterAuthority}
                  setWaterAuthority={setWaterAuthority}
                />
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12}>
            <Card elevation={2}>
              <CardContent>
                <Box display="flex" alignItems="center" mb={2}>
                  <SettingsIcon color="primary" sx={{ mr: 1 }} />
                  <Typography variant="h6" fontWeight="600">
                    Configuration Settings
                  </Typography>
                </Box>
                <Divider sx={{ mb: 3 }} />
                <UpdateForm
                  fields={waterAuthorityFormFields}
                  record={waterAuthority}
                  modelName="water_authority"
                  controller="backflows/water_authorities"
                />
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button variant="contained" color="warning" onClick={handleDeleteClicked}>
              Delete Water Authority
            </Button>
          </Grid>
        </Grid>
        <DocumentationModal
          isOpen={designationDocumentationModalOpen}
          setIsOpen={setDesignationDocumentationModalOpen}
          documentationText={designationDocumentationText}
        />
      </Box>
    </LoadingWrapper>
  );
};

export default WaterAuthorityDetailPage;
